import { useEffect, useRef } from "react";

export const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "a[href]",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// Locks page scroll and restores focus to the opener when the dialog unmounts.
export const useDialogScrollLock = () => {
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, []);
};

// Dialogs can stack (e.g. the login pop-up opening over the bell modal); only
// the most recently mounted one handles keys and focus.
const activeTraps = [];
const isTopTrap = (token) => activeTraps[activeTraps.length - 1] === token;

// Focuses the first control whenever `focusKey` changes, traps Tab inside the
// dialog and calls `onEscape` (when given) for the Escape key.
export const useDialogFocusTrap = (dialogRef, { onEscape, focusKey } = {}) => {
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  const tokenRef = useRef(null);
  if (!tokenRef.current) tokenRef.current = {};

  useEffect(() => {
    const token = tokenRef.current;
    activeTraps.push(token);
    return () => {
      const index = activeTraps.lastIndexOf(token);
      if (index !== -1) activeTraps.splice(index, 1);
    };
  }, []);

  useEffect(() => {
    const token = tokenRef.current;
    const dialog = dialogRef.current;
    if (isTopTrap(token)) dialog?.querySelector(FOCUSABLE_SELECTOR)?.focus();

    const handleKeyDown = (event) => {
      if (!isTopTrap(token)) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onEscapeRef.current?.();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;

      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const keepFocusInDialog = (event) => {
      if (!isTopTrap(token)) return;
      if (dialog && !dialog.contains(event.target)) {
        dialog.querySelector(FOCUSABLE_SELECTOR)?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("focusin", keepFocusInDialog);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("focusin", keepFocusInDialog);
    };
  }, [dialogRef, focusKey]);
};
